import { Box, Stack, Tab, Tabs } from "@mui/material";
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState, useRef } from "react";
import { scrollbarStyle } from "../scrollbarStyle";
import { AnthropometricFields } from "../Anthropometrics/AnthropometricFields";
import { VitalSignsFields } from "../VitalSigns/VitalSignsFields";
import { ImmunizationForm } from "../Immunizations/components/ImmunizationForm";
import { AnthropometricHistory } from "../Anthropometrics/AnthropometricHistory";
import { VitalSignsHistory } from "../VitalSigns/VitalSignsHistory";
import { ImmunizationHistory } from "../Immunizations/ImmunizationHistory";
import { QuestionnairesHistory } from "../Questionnaires/QuestionnairesHistory";
import { WearableDataView } from "../Wearable/WearableDataView";
import { useForm, useFormState } from "react-hook-form";
import {
  CircularLoading,
  PrimaryButton,
  SecondaryButton,
} from "../../../../components/common";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useSessionContext } from "@/contexts/SessionContext";
import { PostImmunizationNarrative } from "@/services/fhir/patientsummary/immunization/PostImmunizationNarrative";
import { PostImmunization } from "@/services/fhir/patientsummary/immunization/PostImmunization";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import LuxorMed from "../../../../components/luxor/LuxorMed";
import { QuestionnairesTab } from "../Questionnaires/QuestionnairesTab";
import { useQuestionnaires } from "@/hooks/useQuestionnaires";
import { useQuestionnairesContractContext } from "@/contexts/QuestionnairesContractContext";
import { WearableObservations } from "@/pages/wearableobservations/WearableObservations";
import { oids } from "@/configs/Settings";

/**
 * Chaves estáveis das abas (não índices numéricos).
 * Evita bug quando a aba Questionários é omitida: o MUI Tabs usa posição 0..N-1,
 * que não coincidia mais com as chaves das abas.
 */
type ObjectiveTabKey =
  | "anthropometrics"
  | "vitalSigns"
  | "immunizations"
  | "questionnaires"
  | "wearable"
  | "fluxHealth";

// Constantes legíveis para comparações (evita typos)
const T = {
  anthropometrics: "anthropometrics" as ObjectiveTabKey,
  vitalSigns: "vitalSigns" as ObjectiveTabKey,
  immunizations: "immunizations" as ObjectiveTabKey,
  questionnaires: "questionnaires" as ObjectiveTabKey,
  wearable: "wearable" as ObjectiveTabKey,
  fluxHealth: "fluxHealth" as ObjectiveTabKey,
};

interface TabPanelProps {
  children?: ReactNode;
  panelId: ObjectiveTabKey;
  value: ObjectiveTabKey;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, panelId, ...other } = props;
  const active = value === panelId;

  return (
    <Box
      role="tabpanel"
      hidden={!active}
      id={`objective-tabpanel-${panelId}`}
      aria-labelledby={`objective-tab-${panelId}`}
      {...other}
      sx={{
        flex: active ? 1 : 0,
        minHeight: 0,
        overflow: "hidden",
        display: active ? "flex" : "none",
        flexDirection: "column",
      }}
    >
      {active && (
        <Box
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            ...scrollbarStyle,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

function a11yProps(tabId: ObjectiveTabKey) {
  return {
    id: `objective-tab-${tabId}`,
    "aria-controls": `objective-tabpanel-${tabId}`,
  };
}

export function ObjectiveData() {
  const { state } = useLocation();
  const { user } = useSessionContext();
  const { setRefetch, loadingSummary, luxorMode, patient } =
    useMedicalCareContext();
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] =
    useState<string>("");

  // CPF do paciente em atendimento — GetObservation precisa do identifier, não do médico logado
  const fluxHealthPatientId = useMemo(() => {
    const fromIdentifier = patient?.identifier?.find(
      (id: { system?: string; value?: string }) =>
        id.system?.includes(oids.cpf)
    )?.value;
    if (fromIdentifier) return fromIdentifier;
    // Fallback: id no formato "oid-cpf" → pega só o CPF
    const rawId = patient?.id as string | undefined;
    if (!rawId) return undefined;
    return rawId.includes("-") ? rawId.split("-").slice(1).join("-") : rawId;
  }, [patient]);

  const [tabValue, setTabValue] = useState<ObjectiveTabKey>(T.anthropometrics);
  const references = state?.encounterReference;
  const methods = useForm();
  const { handleSubmit, control, reset, setValue } = methods;
  const [editImmunization, setEditImmunization] = useState<any>(null);
  const [loadingEdit, setLoadingEdit] = useState<boolean>(false);
  const { dirtyFields } = useFormState({ control });

  const { questionnaires: orgQuestionnaires, loading: loadingOrgQuestionnaires, fetchQuestionnaires } = useQuestionnaires({
    count: 50,
    status: "active",
    tag: "sleep",
  });

  const { contractQuestionnaires, loading: loadingContract } = useQuestionnairesContractContext();
  const hasFetchedRef = useRef<boolean>(false);

  /** Painel da lista de questionários (só quando há itens para exibir). */
  const showQuestionnairesPanel =
    !loadingContract &&
    !!contractQuestionnaires &&
    !loadingOrgQuestionnaires &&
    (orgQuestionnaires?.length ?? 0) > 0;

  /** Aba Questionários visível quando há contrato (igual ao comportamento anterior). */
  const showQuestionnairesTabInHeader =
    !loadingContract && !!contractQuestionnaires;

  useEffect(() => {
    if (contractQuestionnaires && !loadingContract && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchQuestionnaires();
    }

    if (!contractQuestionnaires && !loadingContract) {
      hasFetchedRef.current = false;
    }
  }, [contractQuestionnaires, loadingContract]);

  // Se a aba Questionários deixa de existir, não manter tabValue inválido
  useEffect(() => {
    if (tabValue === T.questionnaires && !showQuestionnairesTabInHeader) {
      setTabValue(T.anthropometrics);
    }
  }, [tabValue, showQuestionnairesTabInHeader]);

  const handleChange = (_: SyntheticEvent, newValue: ObjectiveTabKey) => {
    setTabValue(newValue);
    reset();
    setEditImmunization(null);
  };

  const onSubmitImmunization = async (data: any) => {
    try {
      const immunizationResponse = await PostImmunization(
        {
          ...data?.immunization,
          practitionerId: user?.username,
          practitionerName: user?.name,
        },
        references
      );

      if (data?.immunization?.narrative?.length && immunizationResponse?.id) {
        await PostImmunizationNarrative(
          {
            ...data?.immunization,
            immunizationId: immunizationResponse?.id,
            practitionerId: user?.username,
            practitionerName: user?.name,
          },
          references
        );
      }
      toast.success("Imunização registrada!");
      reset();
    } catch {
      toast.error("Erro ao registrar imunização!");
    } finally {
      reset({
        immunization: {
          vaccine: null,
          date: null,
          narrative: "",
        },
      });

      setRefetch(true);
    }
  };

  const onEditImmunization = async (data: any) => {
    setLoadingEdit(true);
    try {
      // TODO: Implementar PutImmunization
      console.log("Dados a serem editados:", data);
      toast.success("Imunização atualizada!");
    } catch (err) {
      console.log("err", err);
      toast.error("Erro ao atualizar imunização!");
    } finally {
      setLoadingEdit(false);
      reset();
      setEditImmunization(null);
    }
  };

  // Wearable e FluxHealth ocupam a largura inteira (sem painel de histórico ao lado)
  const isFullWidthTab =
    tabValue === T.wearable || tabValue === T.fluxHealth;

  return (
    <>
      <Stack direction="row" width="100%" height="60vh" spacing={2}>
        <Stack
          width={isFullWidthTab ? "100%" : "50%"}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            ...scrollbarStyle,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
            {/* scrollable + setas: com FluxHealth as abas podem estourar a largura */}
            <Tabs
              value={tabValue}
              onChange={handleChange}
              aria-label="objective data tabs"
              variant="scrollable"
              // Setas sempre visíveis: quando some, sobra um vazio à esquerda
              scrollButtons
              allowScrollButtonsMobile
              sx={{
                // Sem rolagem disponível a seta fica apagada, mas ocupa o espaço
                "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
              }}
            >
              <Tab
                label="Antropométricos"
                value={T.anthropometrics}
                {...a11yProps(T.anthropometrics)}
              />
              <Tab
                label="Sinais Vitais"
                value={T.vitalSigns}
                {...a11yProps(T.vitalSigns)}
              />
              <Tab
                label="Imunizações"
                value={T.immunizations}
                {...a11yProps(T.immunizations)}
              />

              {showQuestionnairesTabInHeader && (
                <Tab
                  label="Questionários"
                  value={T.questionnaires}
                  {...a11yProps(T.questionnaires)}
                />
              )}
              {/* <Tab
                label="Google Health"
                value={T.wearable}
                {...a11yProps(T.wearable)}
              /> */}
              <Tab
                label="Dados Wearable"
                value={T.fluxHealth}
                {...a11yProps(T.fluxHealth)}
              />
            </Tabs>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
          <CustomTabPanel value={tabValue} panelId={T.anthropometrics}>
            <AnthropometricFields />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} panelId={T.vitalSigns}>
            <VitalSignsFields />
          </CustomTabPanel>
          {/* Aba de Dados Wearable não possui formulário de entrada por enquanto */}
          <CustomTabPanel value={tabValue} panelId={T.immunizations}>
            <Box sx={{ p: 2 }}>
              {loadingSummary ? (
                <CircularLoading />
              ) : (
                <ImmunizationForm
                  control={control}
                  setValue={setValue}
                  editImmunization={editImmunization}
                />
              )}

              {/* Botões de ação para Imunizações */}
              {tabValue === T.immunizations && (
                <Stack
                  direction="row"
                  width="100%"
                  mt={2}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {!editImmunization ? (
                    <PrimaryButton
                      onClick={handleSubmit(onSubmitImmunization)}
                      width="fit-content"
                      height="40px"
                    >
                      Salvar
                    </PrimaryButton>
                  ) : (
                    <Stack
                      direction="row"
                      width="100%"
                      justifyContent="space-between"
                    >
                      <SecondaryButton
                        width="fit-content"
                        height="40px"
                        onClick={() => {
                          reset();
                          setEditImmunization(null);
                        }}
                      >
                        Cancelar
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={handleSubmit(onEditImmunization)}
                        width="fit-content"
                        height="40px"
                        loading={loadingEdit}
                        disabled={!Object.keys(dirtyFields).length}
                      >
                        Editar
                      </PrimaryButton>
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
          </CustomTabPanel>
          {showQuestionnairesPanel && (
            <CustomTabPanel value={tabValue} panelId={T.questionnaires}>
              <QuestionnairesTab
                setSelectedQuestionnaireId={setSelectedQuestionnaireId}
              />
            </CustomTabPanel>
          )}
          <CustomTabPanel value={tabValue} panelId={T.wearable}>
            <WearableDataView />
          </CustomTabPanel>
          {/* Mesma tela do paciente; GetObservation pelo CPF do paciente em atendimento */}
          <CustomTabPanel value={tabValue} panelId={T.fluxHealth}>
            <WearableObservations
              embedded
              patientId={fluxHealthPatientId}
            />
          </CustomTabPanel>
          </Box>
        </Stack>

        {!isFullWidthTab && (
          <Stack
            width="50%"
            overflow="auto"
            sx={{
              ...scrollbarStyle,
            }}
          >
            {!luxorMode ? (
              <>
                {tabValue === T.anthropometrics && (
                  <AnthropometricHistory />
                )}
                {tabValue === T.vitalSigns && (
                  <VitalSignsHistory />
                )}
                {tabValue === T.immunizations && (
                  <ImmunizationHistory
                    reset={reset}
                    setEditImmunization={setEditImmunization}
                  />
                )}
                {showQuestionnairesPanel &&
                  tabValue === T.questionnaires && (
                    <QuestionnairesHistory
                      selectedQuestionnaireId={selectedQuestionnaireId}
                      setSelectedQuestionnaireId={setSelectedQuestionnaireId}
                    />
                  )}
              </>
            ) : (
              <LuxorMed />
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
}
